import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import * as d3 from 'd3';
import moment from 'moment';
import { getDateRangeArray, getWeekRangeByMonth } from '@/utils/date';
import { appConfigService } from '@/providers/services/app/app-config';

@Component({
    name: 'stacked-bar-chart',
})
export default class StackedBarChartComponent extends Vue {

    @Prop()
    public dataList: any;

    @Prop({ default: 'monthly' })
    public frequency!: string;

    @Prop()
    public activeMonth!: string;

    @Prop()
    public activeYear!: string;

    @Prop()
    public nudgeId!: string;

    @Prop({ default: false })
    public showAverageSessionChart!: boolean;

    @Prop({ default: false })
    public showNudgeTimeSpentChart!: boolean;

    @Prop()
    public nudgeList!: any;

    @Prop({ default: () => [] })
    public selectNudgeInteractionUserIds!: string[];

    public tooltipStyle: any = { toolX: 0, toolY: 0 };

    public tooltipData: any = null;

    public chartWidth = 1060;

    public chartHeight = 420;

    public margin = { top: 20, right: 30, bottom: 60, left: 60 };

    public get startDate() {
        return moment([this.activeYear, parseInt(this.activeMonth, 10) - 1]).toDate();
    }

    public get endDate() {
        return (this.activeMonth === moment().format('MM')
            && this.activeYear === moment().format('YYYY'))
            ? moment().subtract(1, 'day').toDate()
            : moment(this.startDate).endOf('month').toDate();
    }

    public get isCurrentMonthActive() {
        return this.activeMonth === moment().format('MM') && this.activeYear === moment().format('YYYY');
    }

    public get dailyFrequencyLabels() {
        return getDateRangeArray(
            moment(this.startDate).format('YYYY-MM-DD'),
            moment(this.endDate).format('YYYY-MM-DD'));
    }

    public get weeklyFrequencyLabels() {
        const tillCurrentWeek = this.activeMonth === moment().format('MM')
            && this.activeYear === moment().format('YYYY');
        return getWeekRangeByMonth(this.activeMonth, this.activeYear, tillCurrentWeek);
    }

    public get cdnUrl() {
        return appConfigService.getCdnUrl();
    }

    public get nudgeInfo() {
        return appConfigService.getNudgeInfo();
    }

    public get isMonthly() {
        return this.frequency === 'monthly';
    }

    @Watch('dataList', { deep: true })
    public async onChangeData() {
        this.drawStackedBarChart();
    }

    // -----------------------------------------------------------------
    // Hooks

    public mounted() {
        this.drawStackedBarChart();
    }

    // ------------------------------------------------------------------
    // Methods

    public parseChartData(dataList: any[] = []): any[] {

        const dataSet: any[] = [];
        // const isMonthly = this.frequency === 'monthly';
        const labelByFrequency = this.frequency === 'daily'
            ? this.dailyFrequencyLabels
            : this.weeklyFrequencyLabels;

        if (this.isMonthly) {
            // Monthly data processing
            dataList.forEach((item, index) => {
                const nudgeIdToMatch = item.nudge_id || this.nudgeId;
                const nudgeInfo =
                    this.nudgeList.find((nudge: any) => nudge.nudge_id === nudgeIdToMatch) ||
                    this.nudgeInfo[nudgeIdToMatch] || {};

                const iconUrl = (nudgeInfo.thumbnail && nudgeInfo.thumbnail.icon) || nudgeInfo.icon || '';

                dataSet.push({
                    nudgeId: nudgeInfo.name || item.nudge_id,
                    label: item!.nudge_id,
                    pos: index + 1,
                    revisitedNudge: Math.max(0, item.revisit_nudge_count) || 0,
                    acceptedNudge: Math.max(0, item.accepted_nudge_count) || 0,
                    suggestedNudge: Math.max(0, item.suggested_nudge_count) || 0,
                    logo: `${this.cdnUrl}${iconUrl}`,
                });
            });
        } else {
            // Daily or Weekly data processing
            labelByFrequency!.forEach((frequencyLabel: any, index: number) => {
                const label = this.frequency === 'daily'
                    ? moment(frequencyLabel).format('DD')
                    : frequencyLabel.weekLabel;

                // Find matching data based on index or frequency week
                const matchedData = dataList.find((data: any) =>
                    String(data.label) === String(index + 1) ||
                    String(data.label) === String(frequencyLabel.week),
                ) || {};

                const nudgeIdToMatch = matchedData.nudge_id || this.nudgeId;
                const nudgeInfo =
                    this.nudgeList.find((nudge: any) => nudge.nudge_id === nudgeIdToMatch) ||
                    this.nudgeInfo[nudgeIdToMatch] || {};

                const iconUrl = (nudgeInfo.thumbnail && nudgeInfo.thumbnail.icon) || nudgeInfo.icon || '';

                const coordinateData = matchedData
                    ? {
                        nudgeId: nudgeInfo!.name,
                        dateOrWeek: matchedData.label,
                        label,
                        pos: index + 1,
                        revisitedNudge: Math.max(0, matchedData.revisit_nudge_count) || 0,
                        acceptedNudge: Math.max(0, matchedData.accepted_nudge_count) || 0,
                        suggestedNudge: Math.max(0, matchedData.suggested_nudge_count) || 0,
                        logo: `${this.cdnUrl}${iconUrl}`,
                    }
                    : {
                        nudgeId: this.nudgeId ? nudgeInfo!.name : '',
                        label,
                        pos: index + 1,
                        revisitedNudge: 0,
                        acceptedNudge: 0,
                        suggestedNudge: 0,
                        logo: this.nudgeId ? `${this.cdnUrl}${iconUrl}` : '',
                    };
                dataSet.push(coordinateData);
            });
        }
        return dataSet;
    }

    public drawStackedBarChart() {

        d3.select(this.$el).select('.stacked-bar-chart').select('svg').remove();

        const data = this.parseChartData(this.dataList || []);

        const colors = ['#4b8fe2', '#F8BA41', '#E2EFFF'];

        const width = this.chartWidth - this.margin.left - this.margin.right;
        const height = this.chartHeight - this.margin.top - this.margin.bottom;

        // Create SVG container
        const svg = d3
            .select(this.$el)
            .select('.stacked-bar-chart')
            .append('svg')
            .attr('width', this.chartWidth)
            .attr('height', this.chartHeight)
            .append('g')
            .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

        if (!data.length) {
            return this.displayNoDataMessage(svg);
        }

        // X-axixs scales
        const x: any = d3
            .scaleBand()
            .domain(data.map((d: any) => d.pos))
            .range([0, width])
            .padding(0.5);

        const yMaxValue = Math.max(10, d3.max(
            data, (d: any) => d.revisitedNudge + d.acceptedNudge + d.suggestedNudge,
        ) || 0);

        // Y-axis scale
        const y: any = d3
            .scaleLinear()
            .domain([0, yMaxValue <= 10 ? 10 : yMaxValue])
            .range([height, 0]);

        const colorScale: any = d3.scaleOrdinal()
            .domain(['revisitedNudge', 'acceptedNudge', 'suggestedNudge']).range(colors);

        const tickValues = d3.range(0, yMaxValue + 1, yMaxValue / 10);

        // Add gridlines
        svg.selectAll('.grid-line')
            .data(tickValues)
            .enter()
            .append('line')
            .attr('class', 'grid-line')
            .attr('x1', 0)
            .attr('x2', width)
            .attr('y1', (d) => y(d))
            .attr('y2', (d) => y(d))
            .attr('stroke', '#00000026')
            .attr('stroke-dasharray', '2 2');

        // Draw stacked bars
        svg.selectAll('.bar-group')
            .data(data)
            .enter()
            .append('g')
            .attr('class', 'bar-group')
            .attr('transform', (d) => `translate(${x(d.pos)},0)`)
            .each(function(d) {
                const group = d3.select(this);

                // Draw suggestedNudge (bottom bar)
                group
                    .append('rect')
                    .attr('y', y(d.revisitedNudge + d.acceptedNudge + d.suggestedNudge))
                    .attr('height', height - y(d.revisitedNudge + d.acceptedNudge + d.suggestedNudge))
                    .attr('width', x.bandwidth())
                    .attr('fill', colorScale('suggestedNudge'))
                    .style('stroke', '#4b8fe2')
                    .style('stroke-width', '1px')
                    .style('stroke-dasharray', '2,2');

                // Draw acceptedNudge (bottom bar)
                group
                    .append('rect')
                    .attr('y', y(d.revisitedNudge + d.acceptedNudge))
                    .attr('height', height - y(d.revisitedNudge + d.acceptedNudge))
                    .attr('width', x.bandwidth())
                    .attr('fill', colorScale('acceptedNudge'));

                // Draw revisitedNudge (top bar)
                group
                    .append('rect')
                    .attr('y', y(d.revisitedNudge))
                    .attr('height', height - y(d.revisitedNudge))
                    .attr('width', x.bandwidth())
                    .attr('fill', colorScale('revisitedNudge'));
            });

        // Add X-axis
        svg.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(
                d3.axisBottom(x)
                    .tickFormat((d: any) => data[d - 1] ? data[d - 1].label : ''),
            )
            .selectAll('text')
            .style('font-size', '12px')
            .style('font-family', 'Montserrat');

        if (this.isMonthly) {

            // isMonthly remove the text.
            svg.selectAll('text')
                .style('font-size', '12px')
                .style('font-family', 'Montserrat')
                .remove();
            // Now add the images (logos) for each tick
            svg
                .selectAll('.axis-logo')
                .data(data)
                .enter()
                .append('image')
                .attr('x', (d: any) => x(d.pos) + x.bandwidth() / 2 - 12)
                .attr('y', height - -10)
                .attr('width', 20)
                .attr('height', 20)
                .attr('href', (d: any) => d.logo)
                .attr('class', 'axis-logo')
                .attr('preserveAspectRatio', 'xMidYMid slice');
        }

        // Add Y-axis
        svg.append('g')
            .call(
                d3.axisLeft(y).tickValues(tickValues)
                    .tickFormat((d) => {
                        const value = d as number;
                        if (value >= 1000) {
                            return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}k`;
                        }
                        return value % 1 === 0 ? `${value}` : value.toFixed(0);
                    }),
            )
            .selectAll('text')
            .style('font-size', '12px')
            .style('font-family', 'Montserrat');

        const tooltipLine = svg
            .append('line')
            .attr('stroke', '#888')
            .attr('stroke-dasharray', '8 8')
            .style('opacity', 0);

        // Add axis lable
        this.addAxisLabel(svg, height, width);

        svg
            .selectAll('.bar-group')
            .style('cursor', (d: any) => (
                this.selectNudgeInteractionUserIds &&
                    this.selectNudgeInteractionUserIds.length ? 'default' : 'pointer'))
            .on('mousemove', (d: any) => {
                const centerX = x(d.pos) + x.bandwidth() / 2;
                const centerY = y(d.value);

                // Ensure dayIndex is within bounds
                const dayIndex = d.pos - 1;
                if (dayIndex >= 0 && dayIndex < data.length) {
                    this.tooltipData = {
                        ...d,
                        nudgeInfo: [
                            { d, title: 'Total Nudges Recommended', count: d.suggestedNudge || 0, color: 'light-blue' },
                            { title: 'Total Nudges Accepted', count: d.acceptedNudge || 0, color: 'yellow' },
                            { title: 'Total Nudges Revisited', count: d.revisitedNudge || 0, color: 'blue' },
                        ],
                    };

                    // Position the tooltip dynamically
                    const mouseX = centerX + this.margin.left;
                    const mouseY = centerY + this.margin.top;

                    tooltipLine
                        .attr('x1', centerX)
                        .attr('y1', 0)
                        .attr('x2', centerX)
                        .attr('y2', height)
                        .style('opacity', 1);

                    this.tooltipStyle = {
                        toolX: mouseX + 20,
                        toolY: mouseY - 50,
                    };
                }
            })
            .on('mouseleave', () => {
                this.tooltipData = null;
                tooltipLine.style('opacity', 0);
            })
            .on('click', (d: any) => {
                if (this.selectNudgeInteractionUserIds && this.selectNudgeInteractionUserIds.length) { return; }
                this.$emit(this.isMonthly ? 'onSelectNudgeInteraction' : 'onSelectDate', d);
            });
    }

    public displayNoDataMessage(svg: any) {
        // Add "No Data Found" text
        svg.append('text')
            .attr('x', this.chartWidth / 2 - this.margin.left)
            .attr('y', this.chartHeight / 2 - this.margin.top)
            .attr('text-anchor', 'middle')
            .style('font-weight', '600')
            .style('font-family', 'Montserrat')
            .style('text-transform', 'uppercase')
            .style('fill', '#969696')
            .text('No Data Found');
    }

    public addAxisLabel(svg: any, height: number, width: number) {
        const labels = [
            {
                transform: 'translate(-490, 210) rotate(-90)',
                x: 0,
                y: 445,
                text: 'count',
            },
            {
                transform: 'translate(5, 45) rotate(0)',
                x: width / 2 - 50,
                y: height + 10,
                text: this.isMonthly ? 'Nudge Type' : this.frequency === 'daily' ? 'Day' : 'Week',
            },
        ];

        labels.forEach(({ transform, x, y, text }) => {
            svg
                .append('g')
                .attr('transform', transform)
                .append('text')
                .attr('class', 'placeholder')
                .attr('x', x.toString())
                .attr('y', y.toString())
                .style('font-size', '13px')
                .style('font-weight', '600')
                .style('font-family', 'Montserrat')
                .style('text-transform', 'uppercase')
                .style('fill', '#969696')
                .text(text);
        });
    }
}
